import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button, Select, Space, Typography, Spin, Empty, message, Table, Card, Statistic, Modal } from 'antd';
import { PlusOutlined, ReloadOutlined, AppstoreOutlined, UnorderedListOutlined, EditOutlined, RightOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import iconFarm from '../../icons/farm.png';
import { getFarmsApi, addFarmApi, updateFarmApi, deleteFarmApi } from '../../api/farms';
import { FARM_PHOTO } from '../../utils/constants';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import FarmCard from './FarmCard';
import FarmModal from './FarmModal';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const currentYear = dayjs().year();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => ({ value: String(currentYear - i), label: String(currentYear - i) }));

const pinIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41],
});

function LocationModal({ location, name, open, onClose }) {
  const parts = location?.split(',').map(Number);
  const valid = parts?.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]);
  return (
    <Modal title={`📍 ${name}`} open={open} onCancel={onClose} footer={null} width={520} destroyOnClose>
      {valid ? (
        <MapContainer center={parts} zoom={14} style={{ height: 340, borderRadius: 8 }} zoomControl>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker position={parts} icon={pinIcon} />
        </MapContainer>
      ) : (
        <Text type="secondary">No location available</Text>
      )}
    </Modal>
  );
}

export default function Farms() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [farms, setFarms]               = useState([]);
  const [loading, setLoading]           = useState(false);
  const [modalOpen, setModalOpen]       = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingFarm, setEditingFarm]   = useState(null);
  const [years, setYears]               = useState(() => {
    const saved = sessionStorage.getItem('farms_years');
    const parentYears = location.state?.years;
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parentYears) {
        const filtered = parsed.filter((y) => parentYears.includes(y));
        return filtered.length > 0 ? filtered : parentYears;
      }
      return parsed;
    }
    return parentYears || [String(currentYear)];
  });
  const [view, setView]                 = useState('list');
  const [locationFarm, setLocationFarm] = useState(null);

  const fetchFarms = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await getFarmsApi({ years });
      const data = res.data?.data;
      const cropCounts = {};
      (data?.crop || []).forEach((c) => {
        const fid = String(c.khetarId);
        cropCounts[fid] = (cropCounts[fid] || 0) + 1;
      });
      setFarms((data?.farms || []).map((f) => ({ ...f, cropCount: cropCounts[String(f.id)] || 0 })));
    } catch {
      message.error('Failed to load farms');
    } finally {
      setLoading(false);
    }
  }, [years]);

  useEffect(() => { sessionStorage.setItem('farms_years', JSON.stringify(years)); }, [years]);
  useEffect(() => { fetchFarms(); }, [fetchFarms]);

  const handleAdd = async (values) => {
    setModalLoading(true);
    try {
      await addFarmApi(values);
      message.success('Farm added!');
      setModalOpen(false);
      fetchFarms();
    } catch {
      message.error('Failed to add farm');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setModalLoading(true);
    try {
      await updateFarmApi({ ...values, id: editingFarm.id });
      message.success('Farm updated!');
      setModalOpen(false);
      setEditingFarm(null);
      fetchFarms();
    } catch {
      message.error('Failed to update farm');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFarmApi(id);
      message.success('Farm deleted');
      fetchFarms();
    } catch {
      message.error('Failed to delete farm');
    }
  };

  const openEdit = (farm) => { setEditingFarm(farm); setModalOpen(true); };

  const totalExpense = farms.reduce((s, f) => s + (f.expanse || 0), 0);
  const totalSales   = farms.reduce((s, f) => s + (f.selling || 0), 0);
  const totalProfit  = totalSales - totalExpense;

  const listColumns = [
    {
      title: 'Farm', dataIndex: 'name', key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, farm) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {farm.photo ? (
            <img src={FARM_PHOTO(farm.photo)} alt={name} style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: 6, background: '#f0f9f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={iconFarm} alt="" style={{ width: 22, height: 22, opacity: 0.5 }} />
            </div>
          )}
          <div>
            <span style={{ fontWeight: 600, color: '#1a6b2a', cursor: 'pointer', textTransform: 'capitalize' }}>
              {name}
            </span>
            {farm.location && (
              <div>
                <Button
                  type="link" size="small" icon={<EnvironmentOutlined />}
                  style={{ padding: 0, fontSize: 12, color: '#888', height: 'auto' }}
                  onClick={(e) => { e.stopPropagation(); setLocationFarm(farm); }}
                >
                  View location
                </Button>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Number of Crops', dataIndex: 'cropCount', key: 'cropCount',
      sorter: (a, b) => (a.cropCount || 0) - (b.cropCount || 0),
      render: (v) => `🌱 ${v || 0}`, width: 140,
    },
    {
      title: 'Expense', dataIndex: 'expanse', key: 'expanse', width: 120,
      sorter: (a, b) => (a.expanse || 0) - (b.expanse || 0),
      render: (v) => <span style={{ color: '#fa8c16', fontWeight: 600, fontSize: 14 }}>{formatCurrency(v || 0)}</span>,
    },
    {
      title: 'Sales', dataIndex: 'selling', key: 'selling', width: 120,
      sorter: (a, b) => (a.selling || 0) - (b.selling || 0),
      render: (v) => <span style={{ color: '#52c41a', fontWeight: 600, fontSize: 14 }}>{formatCurrency(v || 0)}</span>,
    },
    {
      title: 'Profit / Loss', key: 'profit', width: 130,
      sorter: (a, b) => ((a.selling || 0) - (a.expanse || 0)) - ((b.selling || 0) - (b.expanse || 0)),
      render: (_, farm) => {
        const p = (farm.selling || 0) - (farm.expanse || 0);
        return <span style={{ color: p >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600, fontSize: 14 }}>{p >= 0 ? '+' : '-'}{formatCurrency(Math.abs(p))}</span>;
      },
    },
    {
      title: '', key: 'actions', width: 90,
      render: (_, farm) => (
        <Space size={4}>
          <Button size="small" type="text" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEdit(farm); }} title="Edit" />
          <Button size="small" type="text" onClick={(e) => { e.stopPropagation(); navigate(`/farms/${farm.id}/farm`, { state: { farmName: farm.name, farmLocation: farm.location, years } }); }} style={{ color: '#1a6b2a' }}>Crops <RightOutlined /></Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#1a6b2a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 22, height: 22, backgroundColor: '#1a6b2a', WebkitMask: `url(${iconFarm}) no-repeat center / contain`, mask: `url(${iconFarm}) no-repeat center / contain`, flexShrink: 0 }} />
            {t('my_farms')}
          </Title>
          <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>A Farm is a location where your plots (land) are situated. Add your farm first, then add plots inside it.</div>
        </div>
        <Space wrap>
          <Select mode="multiple" value={years} onChange={setYears} options={YEAR_OPTIONS} placeholder="Year" style={{ minWidth: 160 }} />
          <Button icon={<ReloadOutlined />} onClick={fetchFarms} />
          <Button.Group>
            <Button
              icon={<AppstoreOutlined />}
              type={view === 'grid' ? 'primary' : 'default'}
              onClick={() => setView('grid')}
              style={view === 'grid' ? { background: '#1a6b2a', borderColor: '#1a6b2a' } : {}}
            />
            <Button
              icon={<UnorderedListOutlined />}
              type={view === 'list' ? 'primary' : 'default'}
              onClick={() => setView('list')}
              style={view === 'list' ? { background: '#1a6b2a', borderColor: '#1a6b2a' } : {}}
            />
          </Button.Group>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingFarm(null); setModalOpen(true); }}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>
            {t('add_farm')}
          </Button>
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : farms.length === 0 ? (
        <Empty description="No farms yet. Add your first farm!" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>
            {t('add_farm')}
          </Button>
        </Empty>
      ) : view === 'grid' ? (
        <Row gutter={[16, 16]}>
          {farms.map((farm) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={farm.id}>
              <FarmCard farm={farm} onEdit={openEdit} onDelete={handleDelete} />
            </Col>
          ))}
        </Row>
      ) : (
        <>
          {/* Summary cards */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            {[
              { label: 'Total Expense', value: totalExpense, color: '#fa8c16', bg: '#fff7e6' },
              { label: 'Total Sales',   value: totalSales,   color: '#52c41a', bg: '#f6ffed' },
              { label: totalProfit >= 0 ? 'Total Profit' : 'Total Loss', value: totalProfit, color: totalProfit >= 0 ? '#52c41a' : '#ff4d4f', bg: totalProfit >= 0 ? '#f6ffed' : '#fff1f0' },
            ].map((item) => (
              <Col xs={24} sm={8} key={item.label}>
                <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
                  <Statistic title={item.label} value={formatCurrency(Math.abs(item.value))} valueStyle={{ color: item.color, fontSize: 18, fontWeight: 'bold' }} />
                </Card>
              </Col>
            ))}
          </Row>

          <Card style={{ borderRadius: 10, background: '#fff', overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
            <Table
              dataSource={farms} columns={listColumns} rowKey="id"
              size="middle" pagination={false}
              onRow={(farm) => ({
                onClick: () => navigate(`/farms/${farm.id}/farm`, { state: { farmName: farm.name, farmLocation: farm.location, years } }),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>
        </>
      )}

      <FarmModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingFarm(null); }}
        onSubmit={editingFarm ? handleEdit : handleAdd}
        initialValues={editingFarm}
        loading={modalLoading}
      />

      <LocationModal
        open={!!locationFarm}
        location={locationFarm?.location}
        name={locationFarm?.name}
        onClose={() => setLocationFarm(null)}
      />
    </div>
  );
}
